import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, ButtonBase, Checkbox, Chip, Divider, FormControlLabel, IconButton, InputAdornment,
    Link as MuiLink, MenuItem, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import HomeOutlinedIcon          from '@mui/icons-material/HomeOutlined';
import CreditCardOutlinedIcon    from '@mui/icons-material/CreditCardOutlined';
import StorefrontOutlinedIcon    from '@mui/icons-material/StorefrontOutlined';
import BlockIcon                 from '@mui/icons-material/Block';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import HandshakeOutlinedIcon     from '@mui/icons-material/HandshakeOutlined';
import OpenInNewIcon             from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon         from '@mui/icons-material/DeleteOutline';
import AddIcon                   from '@mui/icons-material/Add';
import { ConfigCard }            from '../style/cards';
import { PdiRow }                from '../style/cards/_PdiHelpers';
import IconPicker                from './IconPicker';

// ────────────── Static option pools ──────────────
const POS_OPTIONS = [
    { value: '0199', label: '0199 — Webshop registrations' },
    { value: '0200', label: '0200 — Counter sales'         },
    { value: '0201', label: '0201 — B2B'                   },
];
const WAREHOUSE_OPTIONS = [
    { value: '01', label: '(01) Lochristi'    },
    { value: '02', label: '(02) Charleroi'    },
    { value: '03', label: '(03) Destelbergen' },
    { value: '04', label: '(04) softtouch'    },
];
const DISCOUNT_TYPES = [
    { code: '999902', label: 'Gift voucher',             defaultEnabled: true,  alwaysOn: false },
    { code: '999903', label: 'Down payment',             defaultEnabled: false, alwaysOn: false },
    { code: '999904', label: 'Value voucher',            defaultEnabled: true,  alwaysOn: false },
    { code: '999905', label: 'Customer discount',        defaultEnabled: true,  alwaysOn: false },
    { code: '999907', label: 'Commercial value voucher', defaultEnabled: true,  alwaysOn: false },
    { code: '999908', label: 'Discount type',            defaultEnabled: false, alwaysOn: false },
    { code: '999909', label: 'Customer discount coupon', defaultEnabled: true,  alwaysOn: false },
];
const SALUTATIONS = [
    { value: '9001', label: '9001 — Mr.'       },
    { value: '9002', label: '9002 — Mrs.'      },
    { value: '9003', label: '9003 — Mx.'       },
    { value: '',     label: '— require choice —' },
];
const COUNTRIES = ['Belgium', 'Netherlands', 'France', 'Germany', 'Luxembourg', 'United Kingdom'];
const PAYMENT_METHOD_CODES = [
    { value: 'msp',     label: 'Type 9 — MultiSafePay payment' },
    { value: 'mollie',  label: 'Type 7 — Mollie payment'        },
    { value: 'stripe',  label: 'Type 5 — Stripe payment'        },
    { value: 'invoice', label: 'Type 1 — Invoice payment'       },
];
const CUSTOMER_CODE_GROUPS = [
    { value: '01', label: '01 — Hobby'                  },
    { value: '02', label: '02 — Language preference'    },
    { value: '03', label: '03 — Profession'             },
    { value: '04', label: '04 — How did you find us?'   },
    { value: '05', label: '05 — Newsletter category'    },
];
const TICKET_TYPES = [
    { key: 'regular', label: 'Regular ticket', help: 'Standard receipt with line items' },
    { key: 'compact', label: 'Compact ticket', help: 'Smaller receipt with summary only' },
    { key: 'message', label: 'Webshop message', help: "Customer's notes, gift message, etc." },
];

const ProcessingTab = ({ theme, t, subTab = 0 }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    // ────────────── Big config object ──────────────
    const [config, setConfig] = useState({
        // GENERAL
        access: {
            b2bOnly:         false,
            posCode:         '0199',
            guestCustomerId: '34',
            allowGuestPickup:false,
        },
        warehouse: '01',
        discountTypes: DISCOUNT_TYPES.reduce((acc, d) => {
            acc[d.code] = { enabled: d.defaultEnabled, acceptAsPayment: d.defaultEnabled, showInEmail: d.defaultEnabled };
            return acc;
        }, {}),
        generalDiscountType: '',
        cart: {
            allowDiscountCodes:       true,
            allowNegativeTotals:      false,
            limitQuantityPerItem:     true,
            maxQuantityPerItem:       10,
            maxUniqueItemsInCart:     40,
        },
        registration: {
            phoneRequired:            true,
            defaultSalutation:        '9002',
            defaultCountry:           'Belgium',
            showPurchaseHistory:      true,
            showCustomerDiscount:     true,
        },
        externalStock: {
            iconName:  'truck-ramp-box',
            iconColor: '#f57c00',
        },
        ticketPrinting: {
            regular: { pickup: true,  shipped: true  },
            compact: { pickup: false, shipped: false },
            message: { pickup: true,  shipped: true  },
        },
        // PAYMENTS
        payments: {
            location:           'onlineAndStore',
            allowInvoice:       true,
            paymentMethodCode:  'msp',
        },
        // SHIPPING
        shipping: {
            method:                  'pickupAndShipping',
            routeExternalThroughShop:false,
            externalStockEnabled:    false,            // gated by Data sync
            defaultCost:             6,
            freeFromAmount:          99,
            shippingCostItem:        '17581401010101',
        },
        // CUSTOMER FIELDS — up to 5
        customerFields: [
            {
                slot: '02',
                codeGroup: '02',
                showOn: { registration: true, account: true, newsletter: true },
                labels: { nl: 'Taal', en: 'Language', fr: 'Langue' },
            },
        ],
    });

    const setSection = (k) => (patch) => setConfig(p => ({ ...p, [k]: { ...(p[k] || {}), ...patch } }));

    return (
        <Box>
            {subTab === 0 && <GeneralPanel    config={config} setConfig={setConfig} setSection={setSection} theme={theme} tr={tr} />}
            {subTab === 1 && <PaymentsPanel   value={config.payments} onChange={setSection('payments')}    theme={theme} tr={tr} />}
            {subTab === 2 && <ShippingPanel   value={config.shipping} onChange={setSection('shipping')}    theme={theme} tr={tr} />}
            {subTab === 3 && <CustomerFieldsPanel
                                value={config.customerFields}
                                onChange={(v) => setConfig(p => ({ ...p, customerFields: v }))}
                                theme={theme} tr={tr} />}
        </Box>
    );
};
ProcessingTab.propTypes = { theme: PropTypes.string, t: PropTypes.func, subTab: PropTypes.number };

// ─────────────────────────────────────────────────────────────────────────
// GENERAL panel — many cards
// ─────────────────────────────────────────────────────────────────────────
const GeneralPanel = ({ config, setConfig, setSection, theme, tr }) => {
    const setAccess       = setSection('access');
    const setCart         = setSection('cart');
    const setRegistration = setSection('registration');
    const setExternal     = setSection('externalStock');
    const setTicket       = (type, channel, val) => setConfig(p => ({
        ...p,
        ticketPrinting: {
            ...p.ticketPrinting,
            [type]: { ...(p.ticketPrinting[type] || {}), [channel]: val },
        },
    }));
    const setDiscount     = (code, key, val) => setConfig(p => ({
        ...p,
        discountTypes: {
            ...p.discountTypes,
            [code]: { ...(p.discountTypes[code] || {}), [key]: val },
        },
    }));

    const guestEnabled = !!config.access.guestCustomerId?.trim();

    return (
        <Stack spacing={2}>
            {/* Access & account creation */}
            <ConfigCard
                title={tr('cfg_proc_access_title', 'Access & account creation')}
                description={tr('cfg_proc_access_desc', 'Who can place orders, and how their customer accounts are set up in your system.')}
                theme={theme}
            >
                <Stack spacing={1.25} divider={<Divider />}>
                    <PdiRow
                        label={tr('cfg_proc_b2b_only', 'B2B-only mode')}
                        help={tr('cfg_proc_b2b_only_help', "Restrict checkout to registered B2B customers only. Other visitors can still browse but can't order.")}
                        control={<Switch color="success" size="small"
                            checked={!!config.access.b2bOnly}
                            onChange={(e) => setAccess({ b2bOnly: e.target.checked })} />}
                    />
                    <PdiRow
                        label={tr('cfg_proc_pos_code', 'Register new customers under POS')}
                        help={tr('cfg_proc_pos_code_help', 'The point-of-sale identifier in your system that new customer accounts are linked to. Pulled from your POS list.')}
                        control={
                            <TextField select size="small"
                                value={config.access.posCode}
                                onChange={(e) => setAccess({ posCode: e.target.value })}
                                sx={{ minWidth: 260 }}
                            >
                                {POS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_guest_id', 'Guest customer ID')}
                        help={tr('cfg_proc_guest_id_help', 'Fill in a customer ID to allow visitors to place orders without creating an account. Their orders will be linked to this generic customer record. Leave empty to require account creation for all orders.')}
                        control={
                            <TextField size="small"
                                value={config.access.guestCustomerId}
                                onChange={(e) => setAccess({ guestCustomerId: e.target.value })}
                                sx={{ width: 100 }}
                            />
                        }
                    />
                    <Box sx={{ opacity: guestEnabled ? 1 : 0.5, pointerEvents: guestEnabled ? 'auto' : 'none' }}>
                        <PdiRow
                            indent
                            label={tr('cfg_proc_guest_pickup', 'Allow guest pickup orders')}
                            help={tr('cfg_proc_guest_pickup_help', "When off, guests can still order but only for shipping — pickup orders require an account. When on, guests can order both ways. Has no effect if the guest customer ID above is empty.")}
                            control={<Switch color="success" size="small"
                                checked={!!config.access.allowGuestPickup}
                                onChange={(e) => setAccess({ allowGuestPickup: e.target.checked })} />}
                        />
                    </Box>
                </Stack>
            </ConfigCard>

            {/* Warehouse */}
            <ConfigCard
                title={tr('cfg_proc_warehouse_title', 'Warehouse')}
                description={tr('cfg_proc_warehouse_desc', 'The warehouse where webshop orders are created in your inventory system.')}
                theme={theme}
            >
                <PdiRow
                    label={tr('cfg_proc_warehouse_label', 'Order warehouse')}
                    help={tr('cfg_proc_warehouse_help', 'Pick one warehouse — all webshop orders are registered here')}
                    control={
                        <TextField select size="small"
                            value={config.warehouse}
                            onChange={(e) => setConfig(p => ({ ...p, warehouse: e.target.value }))}
                            sx={{ minWidth: 220 }}
                        >
                            {WAREHOUSE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                    }
                />
            </ConfigCard>

            {/* Discount type & vouchers — table */}
            <ConfigCard
                title={tr('cfg_proc_disc_title', 'Discount type & vouchers')}
                description={tr('cfg_proc_disc_desc', 'Which discount types and vouchers are accepted, and which are added to the order confirmation email. Each row represents a discount type defined in your back office.')}
                theme={theme}
            >
                <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    {/* Header */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 100px 140px 120px', alignItems: 'center', gap: 2, mb: 1, px: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                            {tr('cfg_proc_disc_col_type', 'DISCOUNT TYPE')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_disc_col_enabled', 'ENABLED')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_disc_col_payment', 'ACCEPT AS PAYMENT')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_disc_col_email', 'SHOW IN EMAIL')}
                        </Typography>
                    </Box>
                    <Stack spacing={0.5}>
                        {DISCOUNT_TYPES.map(d => {
                            const row = config.discountTypes[d.code] || {};
                            const enabled = !!row.enabled;
                            const dim     = !enabled;
                            return (
                                <Box key={d.code} sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 100px 140px 120px',
                                    alignItems: 'center', gap: 2,
                                    px: 1, py: 0.75,
                                    borderRadius: 1,
                                    backgroundColor: '#fff',
                                    border: '1px solid', borderColor: 'divider',
                                    opacity: dim ? 0.6 : 1,
                                }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.label}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {tr('cfg_proc_disc_code', 'Code')}: {d.code}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={enabled} onChange={(e) => setDiscount(d.code, 'enabled', e.target.checked)} />
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={!!row.acceptAsPayment} onChange={(e) => setDiscount(d.code, 'acceptAsPayment', e.target.checked)} />
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={!!row.showInEmail} onChange={(e) => setDiscount(d.code, 'showInEmail', e.target.checked)} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>

                <PdiRow
                    label={tr('cfg_proc_general_disc', 'General discount type')}
                    help={tr('cfg_proc_general_disc_help', "The discount type used for general discounts on orders, pulled from your back office. Leave empty if you don't want a default.")}
                    control={
                        <TextField select size="small"
                            value={config.generalDiscountType}
                            onChange={(e) => setConfig(p => ({ ...p, generalDiscountType: e.target.value }))}
                            sx={{ minWidth: 220 }}
                        >
                            <MenuItem value="">— {tr('cfg_proc_general_disc_none', 'None')} —</MenuItem>
                            {DISCOUNT_TYPES.map(d => <MenuItem key={d.code} value={d.code}>{d.label}</MenuItem>)}
                        </TextField>
                    }
                />
            </ConfigCard>

            {/* Cart behavior */}
            <ConfigCard
                title={tr('cfg_proc_cart_title', 'Cart behavior')}
                description={tr('cfg_proc_cart_desc', 'How the shopping cart works for visitors.')}
                theme={theme}
            >
                <Stack spacing={1.25} divider={<Divider />}>
                    <PdiRow
                        label={tr('cfg_proc_cart_codes', 'Allow discount codes in cart')}
                        help={tr('cfg_proc_cart_codes_help', 'Visitors can enter a coupon or voucher code at checkout')}
                        control={<Switch color="success" size="small"
                            checked={!!config.cart.allowDiscountCodes}
                            onChange={(e) => setCart({ allowDiscountCodes: e.target.checked })} />}
                    />
                    <Box sx={{ opacity: config.cart.allowDiscountCodes ? 1 : 0.5, pointerEvents: config.cart.allowDiscountCodes ? 'auto' : 'none' }}>
                        <PdiRow
                            indent
                            label={tr('cfg_proc_cart_negative', 'Allow negative cart totals')}
                            help={tr('cfg_proc_cart_negative_help', 'If a discount exceeds the order total, the cart can show a negative balance. Most shops should leave this off.')}
                            control={<Switch color="success" size="small"
                                checked={!!config.cart.allowNegativeTotals}
                                onChange={(e) => setCart({ allowNegativeTotals: e.target.checked })} />}
                        />
                    </Box>
                    <PdiRow
                        label={tr('cfg_proc_cart_limit_qty', 'Limit quantity per item')}
                        help={tr('cfg_proc_cart_limit_qty_help', "When on, a customer can't add more than the limit per item. When off, the limit equals the available stock.")}
                        control={<Switch color="success" size="small"
                            checked={!!config.cart.limitQuantityPerItem}
                            onChange={(e) => setCart({ limitQuantityPerItem: e.target.checked })} />}
                    />
                    <Box sx={{ opacity: config.cart.limitQuantityPerItem ? 1 : 0.5, pointerEvents: config.cart.limitQuantityPerItem ? 'auto' : 'none' }}>
                        <PdiRow
                            indent
                            label={tr('cfg_proc_cart_max_qty', 'Maximum quantity per item')}
                            control={
                                <TextField type="number" size="small"
                                    value={config.cart.maxQuantityPerItem}
                                    onChange={(e) => setCart({ maxQuantityPerItem: Math.max(1, parseInt(e.target.value) || 1) })}
                                    InputProps={{ endAdornment: <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>{tr('cfg_proc_cart_items', 'items')}</InputAdornment> }}
                                    sx={{ width: 180 }}
                                />
                            }
                        />
                    </Box>
                    <PdiRow
                        label={tr('cfg_proc_cart_max_unique', 'Maximum unique items in cart')}
                        help={tr('cfg_proc_cart_max_unique_help', 'How many different products a single cart can contain')}
                        control={
                            <TextField type="number" size="small"
                                value={config.cart.maxUniqueItemsInCart}
                                onChange={(e) => setCart({ maxUniqueItemsInCart: Math.max(1, parseInt(e.target.value) || 1) })}
                                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>{tr('cfg_proc_cart_unique_items', 'unique items')}</InputAdornment> }}
                                sx={{ width: 220 }}
                            />
                        }
                    />
                </Stack>
            </ConfigCard>

            {/* Registration form */}
            <ConfigCard
                title={tr('cfg_proc_reg_title', 'Registration form')}
                description={tr('cfg_proc_reg_desc', 'What information visitors are asked for when registering or visiting their account page.')}
                theme={theme}
            >
                <Stack spacing={1.25} divider={<Divider />}>
                    <PdiRow
                        label={tr('cfg_proc_reg_phone', 'Phone number is required')}
                        help={tr('cfg_proc_reg_phone_help', 'Visitors must provide a phone number when registering')}
                        control={<Switch color="success" size="small"
                            checked={!!config.registration.phoneRequired}
                            onChange={(e) => setRegistration({ phoneRequired: e.target.checked })} />}
                    />
                    <PdiRow
                        label={tr('cfg_proc_reg_salutation', 'Default salutation')}
                        help={tr('cfg_proc_reg_salutation_help', "Pre-fill this salutation if a customer doesn't pick one. Leave blank to require a choice.")}
                        control={
                            <TextField select size="small"
                                value={config.registration.defaultSalutation}
                                onChange={(e) => setRegistration({ defaultSalutation: e.target.value })}
                                sx={{ minWidth: 200 }}
                            >
                                {SALUTATIONS.map(o => <MenuItem key={o.value || 'none'} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_reg_country', 'Default country')}
                        help={tr('cfg_proc_reg_country_help', 'Pre-fill this country on registration forms. Leave blank to require a choice.')}
                        control={
                            <TextField select size="small"
                                value={config.registration.defaultCountry}
                                onChange={(e) => setRegistration({ defaultCountry: e.target.value })}
                                sx={{ minWidth: 200 }}
                            >
                                <MenuItem value="">— {tr('cfg_proc_reg_country_none', 'require choice')} —</MenuItem>
                                {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_reg_history', 'Show purchase history on account page')}
                        help={tr('cfg_proc_reg_history_help', 'Customers can see their past orders when logged in')}
                        control={<Switch color="success" size="small"
                            checked={!!config.registration.showPurchaseHistory}
                            onChange={(e) => setRegistration({ showPurchaseHistory: e.target.checked })} />}
                    />
                    <PdiRow
                        label={tr('cfg_proc_reg_disc', 'Show current customer discount on account page')}
                        help={tr('cfg_proc_reg_disc_help', 'Customers see the discount tier they currently qualify for')}
                        control={<Switch color="success" size="small"
                            checked={!!config.registration.showCustomerDiscount}
                            onChange={(e) => setRegistration({ showCustomerDiscount: e.target.checked })} />}
                    />
                </Stack>
            </ConfigCard>

            {/* External-stock indicator */}
            <ConfigCard
                title={tr('cfg_proc_extstock_title', 'External-stock indicator')}
                description={tr('cfg_proc_extstock_desc', 'Icon shown on products that are out of stock locally but can still be ordered from an external supplier. These orders may take longer to deliver.')}
                theme={theme}
            >
                <PdiRow
                    label={tr('cfg_proc_extstock_icon', 'Indicator icon')}
                    help={tr('cfg_proc_extstock_icon_help', 'Click to browse icons. Use the search to filter.')}
                    align="top"
                    control={
                        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
                            <IconPicker
                                value={config.externalStock.iconName}
                                color={config.externalStock.iconColor}
                                onChange={(name) => setExternal({ iconName: name })}
                                label={tr('cfg_proc_extstock_selected', 'Selected icon')}
                                placeholderLabel={tr('cfg_proc_extstock_pick', 'Pick icon')}
                            />
                            {/* Color picker pill — mirrors the IconPicker's structure
                                (label + control on the left, text on the right) so
                                the two sit at the same height and baseline. */}
                            <Box component="label" sx={{
                                display: 'flex', alignItems: 'center', gap: 1.25,
                                p: 1, borderRadius: 1.5,
                                border: '1px solid', borderColor: 'divider',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                minWidth: 140,
                                transition: 'all 0.15s',
                                '&:hover': { borderColor: 'primary.main' },
                            }}>
                                <Box sx={{
                                    position: 'relative',
                                    width: 36, height: 36, borderRadius: 1,
                                    border: '1px solid rgba(0,0,0,0.18)',
                                    backgroundColor: config.externalStock.iconColor,
                                    flexShrink: 0,
                                }}>
                                    <input
                                        type="color"
                                        value={config.externalStock.iconColor}
                                        onChange={(e) => setExternal({ iconColor: e.target.value })}
                                        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                                        {tr('cfg_proc_extstock_color', 'Color')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontFamily: 'monospace' }}>
                                        {(config.externalStock.iconColor || '').toUpperCase()}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    }
                />
            </ConfigCard>

            {/* Ticket printing — table */}
            <ConfigCard
                title={tr('cfg_proc_tickets_title', 'Ticket printing')}
                description={tr('cfg_proc_tickets_desc', 'Which tickets get printed when an order arrives. Pickup orders and shipped orders can be configured separately.')}
                theme={theme}
            >
                <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    {/* Header */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px', alignItems: 'center', gap: 2, mb: 1, px: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                            {tr('cfg_proc_tickets_col_type', 'TICKET TYPE')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_tickets_col_pickup', 'PICKUP ORDERS')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_tickets_col_shipped', 'SHIPPED ORDERS')}
                        </Typography>
                    </Box>
                    <Stack spacing={0.5}>
                        {TICKET_TYPES.map(tk => {
                            const row = config.ticketPrinting[tk.key] || {};
                            return (
                                <Box key={tk.key} sx={{
                                    display: 'grid', gridTemplateColumns: '1fr 160px 160px', alignItems: 'center', gap: 2,
                                    px: 1, py: 0.75, borderRadius: 1, backgroundColor: '#fff',
                                    border: '1px solid', borderColor: 'divider',
                                }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tk.label}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tk.help}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={!!row.pickup}  onChange={(e) => setTicket(tk.key, 'pickup',  e.target.checked)} />
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={!!row.shipped} onChange={(e) => setTicket(tk.key, 'shipped', e.target.checked)} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </ConfigCard>
        </Stack>
    );
};
GeneralPanel.propTypes = { config: PropTypes.object, setConfig: PropTypes.func, setSection: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// PAYMENTS panel
// ─────────────────────────────────────────────────────────────────────────
const PAYMENT_LOCATIONS = [
    { value: 'inStore',         icon: <HomeOutlinedIcon />,       i18nKey: 'cfg_proc_pay_in_store',       fallback: 'In-store only',          descKey: 'cfg_proc_pay_in_store_desc',       descFallback: 'Pay in person when picking up' },
    { value: 'onlineAndStore',  icon: <CreditCardOutlinedIcon />, i18nKey: 'cfg_proc_pay_online_store',   fallback: 'Online & in-store',      descKey: 'cfg_proc_pay_online_store_desc',   descFallback: 'Customer chooses at checkout' },
    { value: 'onlineOnly',      icon: <StorefrontOutlinedIcon />, i18nKey: 'cfg_proc_pay_online_only',    fallback: 'Online only',            descKey: 'cfg_proc_pay_online_only_desc',    descFallback: 'All payments through the online provider' },
    { value: 'noCheckout',      icon: <BlockIcon />,              i18nKey: 'cfg_proc_pay_no_checkout',    fallback: 'No checkout possible',   descKey: 'cfg_proc_pay_no_checkout_desc',    descFallback: 'Catalogue / lookbook only — no orders' },
];

const PaymentsPanel = ({ value = {}, onChange, theme, tr }) => (
    <Stack spacing={2}>
        <ConfigCard
            title={tr('cfg_proc_pay_title', 'Payment options')}
            description={tr('cfg_proc_pay_desc', 'Where customers can pay for their orders.')}
            theme={theme}
        >
            <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {tr('cfg_proc_pay_where', 'Where customers can pay')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                    {tr('cfg_proc_pay_where_help', 'Pick one. Affects which payment options appear at checkout.')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                    {PAYMENT_LOCATIONS.map(opt => {
                        const sel = value.location === opt.value;
                        return (
                            <ButtonBase
                                key={opt.value}
                                onClick={() => onChange({ location: opt.value })}
                                sx={{
                                    flexDirection: 'column', alignItems: 'flex-start', gap: 1,
                                    p: 1.75, borderRadius: 1.5,
                                    border: '1.5px solid',
                                    borderColor: sel ? 'primary.main' : 'divider',
                                    backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                                    color: sel ? 'primary.main' : 'text.primary',
                                    textAlign: 'left',
                                    minHeight: 120,
                                }}
                            >
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: 1,
                                    backgroundColor: sel ? 'primary.main' : 'rgba(0,0,0,0.04)',
                                    color: sel ? '#fff' : 'text.secondary',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>{opt.icon}</Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {tr(opt.i18nKey, opt.fallback)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: sel ? 'primary.main' : 'text.secondary', lineHeight: 1.3 }}>
                                    {tr(opt.descKey, opt.descFallback)}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Box>
            </Box>

            <Divider />

            <PdiRow
                label={tr('cfg_proc_pay_invoice', 'Allow customers to request an invoice')}
                help={tr('cfg_proc_pay_invoice_help', 'Adds a checkbox at checkout where customers can ask for an invoice. Useful for B2B-like behaviour even on B2C shops.')}
                control={<Switch color="success" size="small"
                    checked={!!value.allowInvoice}
                    onChange={(e) => onChange({ allowInvoice: e.target.checked })} />}
            />

            <PdiRow
                label={tr('cfg_proc_pay_method_code', 'Payment method code')}
                help={tr('cfg_proc_pay_method_code_help', 'The code used internally to associate online payments with a payment type. Pulled from your back office.')}
                control={
                    <TextField select size="small"
                        value={value.paymentMethodCode || ''}
                        onChange={(e) => onChange({ paymentMethodCode: e.target.value })}
                        sx={{ minWidth: 260 }}
                    >
                        {PAYMENT_METHOD_CODES.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                }
            />
        </ConfigCard>
    </Stack>
);
PaymentsPanel.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// SHIPPING & PICKUP panel
// ─────────────────────────────────────────────────────────────────────────
const DELIVERY_OPTIONS = [
    { value: 'pickupOnly',         icon: <HomeOutlinedIcon />,        i18nKey: 'cfg_proc_ship_pickup_only',  fallback: 'Pickup only',     descKey: 'cfg_proc_ship_pickup_only_desc',  descFallback: 'Customer collects from store'    },
    { value: 'shippingOnly',       icon: <LocalShippingOutlinedIcon />,i18nKey: 'cfg_proc_ship_ship_only',   fallback: 'Shipping only',   descKey: 'cfg_proc_ship_ship_only_desc',    descFallback: 'Always shipped to customer'      },
    { value: 'pickupAndShipping',  icon: <HandshakeOutlinedIcon />,   i18nKey: 'cfg_proc_ship_both',         fallback: 'Pickup & shipping', descKey: 'cfg_proc_ship_both_desc',       descFallback: 'Customer chooses at checkout'    },
];

const ShippingPanel = ({ value = {}, onChange, theme, tr }) => {
    const externalEnabled = !!value.externalStockEnabled;
    return (
        <Stack spacing={2}>
            <ConfigCard
                title={tr('cfg_proc_ship_title', 'Delivery options')}
                description={tr('cfg_proc_ship_desc', 'How customers can receive their orders.')}
                theme={theme}
            >
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tr('cfg_proc_ship_methods', 'Available delivery methods')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                        {tr('cfg_proc_ship_methods_help', 'Pick one. The customer chooses at checkout if both are allowed.')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                        {DELIVERY_OPTIONS.map(opt => {
                            const sel = value.method === opt.value;
                            return (
                                <ButtonBase
                                    key={opt.value}
                                    onClick={() => onChange({ method: opt.value })}
                                    sx={{
                                        flexDirection: 'column', alignItems: 'flex-start', gap: 1,
                                        p: 1.75, borderRadius: 1.5,
                                        border: '1.5px solid',
                                        borderColor: sel ? 'primary.main' : 'divider',
                                        backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                                        color: sel ? 'primary.main' : 'text.primary',
                                        textAlign: 'left',
                                        minHeight: 110,
                                    }}
                                >
                                    <Box sx={{
                                        width: 32, height: 32, borderRadius: 1,
                                        backgroundColor: sel ? 'primary.main' : 'rgba(0,0,0,0.04)',
                                        color: sel ? '#fff' : 'text.secondary',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>{opt.icon}</Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {tr(opt.i18nKey, opt.fallback)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: sel ? 'primary.main' : 'text.secondary', lineHeight: 1.3 }}>
                                        {tr(opt.descKey, opt.descFallback)}
                                    </Typography>
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                {!externalEnabled && (
                    <Alert severity="warning" icon={false}>
                        <Typography variant="body2">
                            {tr('cfg_proc_ship_ext_alert_pre', 'The setting below requires')}{' '}
                            <Box component="strong" sx={{ color: 'warning.dark' }}>
                                {tr('cfg_proc_ship_ext_alert_term', 'external stock')}
                            </Box>{' '}
                            {tr('cfg_proc_ship_ext_alert_post', "to be enabled in Data sync. It's currently disabled there, so this option is unavailable.")}
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ opacity: externalEnabled ? 1 : 0.5, pointerEvents: externalEnabled ? 'auto' : 'none' }}>
                    <PdiRow
                        label={tr('cfg_proc_ship_route_external', 'Route external orders through the shop first')}
                        help={tr('cfg_proc_ship_route_external_help', 'When an order contains items from an external supplier, ship them to the shop before forwarding to the customer. Helps with quality control.')}
                        control={<Switch color="success" size="small"
                            checked={!!value.routeExternalThroughShop}
                            onChange={(e) => onChange({ routeExternalThroughShop: e.target.checked })} />}
                    />
                </Box>
            </ConfigCard>

            <ConfigCard
                title={tr('cfg_proc_ship_costs_title', 'Shipping costs')}
                description={tr('cfg_proc_ship_costs_desc', 'Default shipping costs. Set country-specific rates in the dedicated shipping rates page.')}
                theme={theme}
            >
                <Stack spacing={1.25} divider={<Divider />}>
                    <PdiRow
                        label={tr('cfg_proc_ship_default_cost', 'Default shipping cost')}
                        help={tr('cfg_proc_ship_default_cost_help', 'Charged on shipped orders, unless overridden by a country-specific rate')}
                        control={
                            <TextField type="number" size="small"
                                value={value.defaultCost ?? 0}
                                onChange={(e) => onChange({ defaultCost: Math.max(0, parseFloat(e.target.value) || 0) })}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                sx={{ width: 130 }}
                            />
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_ship_free_threshold', 'Free shipping threshold')}
                        help={tr('cfg_proc_ship_free_threshold_help', 'Orders above this amount get free shipping. Set to 0 to never offer free shipping.')}
                        control={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField type="number" size="small"
                                    value={value.freeFromAmount ?? 0}
                                    onChange={(e) => onChange({ freeFromAmount: Math.max(0, parseFloat(e.target.value) || 0) })}
                                    InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                    sx={{ width: 130 }}
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {tr('cfg_proc_ship_and_above', 'and above')}
                                </Typography>
                            </Box>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_ship_country_rates', 'Country-specific shipping rates')}
                        help={tr('cfg_proc_ship_country_rates_help', 'Different rates and free-shipping thresholds per country')}
                        control={
                            <Button variant="outlined" size="small"
                                startIcon={<OpenInNewIcon fontSize="small" />}
                                sx={{ textTransform: 'none', borderRadius: 1.5 }}
                            >
                                {tr('cfg_proc_ship_open_rates', 'Open shipping rates page')}
                            </Button>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_ship_cost_item', 'Shipping cost item')}
                        help={tr('cfg_proc_ship_cost_item_help', 'The internal item barcode used to register shipping costs on orders')}
                        control={
                            <TextField size="small"
                                value={value.shippingCostItem || ''}
                                onChange={(e) => onChange({ shippingCostItem: e.target.value })}
                                sx={{ width: 220 }}
                            />
                        }
                    />
                </Stack>
            </ConfigCard>
        </Stack>
    );
};
ShippingPanel.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// CUSTOMER FIELDS panel
// ─────────────────────────────────────────────────────────────────────────
const MAX_CUSTOMER_FIELDS = 5;

const CustomerFieldsPanel = ({ value = [], onChange, theme, tr }) => {
    const remaining = MAX_CUSTOMER_FIELDS - value.length;
    const updateField = (idx, patch) => onChange(value.map((f, i) => i === idx ? { ...f, ...patch } : f));
    const updateLabel = (idx, lang, val) => updateField(idx, { labels: { ...(value[idx].labels || {}), [lang]: val } });
    const updateShowOn = (idx, key, val) => updateField(idx, { showOn: { ...(value[idx].showOn || {}), [key]: val } });
    const removeField = (idx) => onChange(value.filter((_, i) => i !== idx));
    const addField = () => {
        if (value.length >= MAX_CUSTOMER_FIELDS) return;
        const nextSlot = String(value.length + 2).padStart(2, '0');
        onChange([...value, {
            slot: nextSlot,
            codeGroup: '',
            showOn: { registration: true, account: true, newsletter: false },
            labels: { nl: '', en: '', fr: '' },
        }]);
    };

    return (
        <Stack spacing={2}>
            <ConfigCard
                title={tr('cfg_proc_cf_title', 'Custom customer fields')}
                description={tr('cfg_proc_cf_desc', 'Up to 5 extra fields that show on registration, account, or newsletter forms. Each maps to a customer code group in your back office. The list of available codes is fetched from your system — values can only be edited there.')}
                theme={theme}
            >
                <Stack spacing={1.5}>
                    {value.map((field, idx) => (
                        <Paper key={idx} variant="outlined" sx={{
                            p: 2, borderRadius: 2,
                            backgroundColor: 'rgba(0,0,0,0.02)',
                        }}>
                            {/* Field header — number badge + "Field N" + Slot xx */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{
                                        width: 24, height: 24, borderRadius: '50%',
                                        backgroundColor: 'rgba(25,118,210,0.08)',
                                        color: 'primary.main',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: '0.78rem',
                                    }}>{idx + 1}</Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {tr('cfg_proc_cf_field', 'Field')} {idx + 1}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`${tr('cfg_proc_cf_slot', 'Slot')} ${field.slot}`}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', backgroundColor: 'transparent', border: 'none' }}
                                />
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                {/* Customer code group dropdown */}
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
                                        {tr('cfg_proc_cf_code_group', 'CUSTOMER CODE GROUP')}
                                    </Typography>
                                    <TextField select size="small" fullWidth
                                        value={field.codeGroup || ''}
                                        onChange={(e) => updateField(idx, { codeGroup: e.target.value })}
                                        sx={{ backgroundColor: '#fff' }}
                                    >
                                        <MenuItem value="">— {tr('cfg_proc_cf_select_group', 'select group')} —</MenuItem>
                                        {CUSTOMER_CODE_GROUPS.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                                    </TextField>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                        {tr('cfg_proc_cf_edit_in_office', 'Edit the available values in your back office')}
                                    </Typography>
                                </Box>

                                {/* Show on checkboxes */}
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
                                        {tr('cfg_proc_cf_show_on', 'SHOW THIS FIELD ON')}
                                    </Typography>
                                    <Stack spacing={0}>
                                        {[
                                            { key: 'registration', label: tr('cfg_proc_cf_show_reg',  'Registration screen')      },
                                            { key: 'account',      label: tr('cfg_proc_cf_show_acc',  'Customer account page')    },
                                            { key: 'newsletter',   label: tr('cfg_proc_cf_show_news', 'Newsletter signup pop-up') },
                                        ].map(opt => (
                                            <FormControlLabel
                                                key={opt.key}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={!!field.showOn?.[opt.key]}
                                                        onChange={(e) => updateShowOn(idx, opt.key, e.target.checked)}
                                                    />
                                                }
                                                label={<Typography variant="body2">{opt.label}</Typography>}
                                                sx={{ ml: -0.75 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>

                            {/* Label per language */}
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
                                    {tr('cfg_proc_cf_label', 'LABEL SHOWN TO CUSTOMERS')}
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                                    {[
                                        { code: 'nl', short: 'NL', label: tr('cfg_proc_cf_lang_nl', 'Dutch')   },
                                        { code: 'en', short: 'GB', label: tr('cfg_proc_cf_lang_en', 'English') },
                                        { code: 'fr', short: 'FR', label: tr('cfg_proc_cf_lang_fr', 'French')  },
                                    ].map(l => (
                                        <Box key={l.code} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, backgroundColor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}>
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, fontSize: '0.65rem' }}>{l.short}</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 50 }}>{l.label}</Typography>
                                            <TextField
                                                size="small" variant="standard"
                                                value={field.labels?.[l.code] || ''}
                                                onChange={(e) => updateLabel(idx, l.code, e.target.value)}
                                                sx={{ flex: 1, '& .MuiInput-input': { fontSize: '0.85rem', py: 0.5 } }}
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined" color="error" size="small"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => removeField(idx)}
                                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                >
                                    {tr('cfg_proc_cf_remove', 'Remove field')}
                                </Button>
                            </Box>
                        </Paper>
                    ))}

                    {remaining > 0 && (
                        <ButtonBase
                            onClick={addField}
                            sx={{
                                width: '100%', py: 1.5,
                                border: '1.5px dashed', borderColor: 'divider',
                                borderRadius: 1.5,
                                color: 'text.secondary',
                                gap: 0.75,
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                            }}
                        >
                            <AddIcon fontSize="small" />
                            <Typography variant="body2">
                                {tr('cfg_proc_cf_add', 'Add another field')} ({remaining} {tr('cfg_proc_cf_of', 'of')} {MAX_CUSTOMER_FIELDS} {tr('cfg_proc_cf_slots_remaining', 'slots remaining')})
                            </Typography>
                        </ButtonBase>
                    )}
                </Stack>
            </ConfigCard>
        </Stack>
    );
};
CustomerFieldsPanel.propTypes = { value: PropTypes.array, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

export default ProcessingTab;
